import type { CSSProperties } from "react";
import { cn } from "@/lib/utils";

// Generic, non-semantic pill. One component covers every current inline-styled
// badge/choice in the app so call sites stop hand-rolling `style={{ color, background }}`.
//
// Two shapes share this component, switched by `shape`:
//  - "badge" (default): the rounded 20px `.badge` pill (optionally with a `.badge-dot`).
//    Colour comes from `variant` → a (text token, soft-background token) pair.
//  - "choice": the compact `.choice-badge` chip used for club 1st/2nd preference and
//    the ops "new"/fresh marker. Its palette is fixed by `.choice-1`/`.choice-2` in CSS,
//    so it ignores `variant`/`dot` and is driven by `choice` (1 | 2).
//
// All colour tokens live in globals.css; this component only emits existing classes
// and CSS-var references — it adds no new CSS and hard-codes no hex/px.

export type TagVariant = "primary" | "success" | "warning" | "danger" | "neutral";

/**
 * variant → { text colour token, soft background token }. These reproduce the
 * exact inline pairs the call sites use today (e.g. ClubStatusPill active =
 * success/success-soft, the ops queue count = primary/primary-soft).
 */
const VARIANT_TOKENS: Record<TagVariant, { color: string; background: string }> = {
  primary: { color: "var(--primary)", background: "var(--primary-soft)" },
  success: { color: "var(--success)", background: "var(--success-soft)" },
  warning: { color: "var(--warning)", background: "var(--warning-soft)" },
  danger: { color: "var(--danger)", background: "var(--danger-soft)" },
  neutral: { color: "var(--text-muted)", background: "var(--surface-3)" },
};

export interface TagProps {
  /** Pill shape. Defaults to the rounded `.badge` pill. */
  shape?: "badge" | "choice";
  /**
   * Colour pair for the `.badge` shape. Ignored when `shape="choice"`.
   * Defaults to `primary`.
   */
  variant?: TagVariant;
  /** Render a leading `.badge-dot` tinted to the variant text colour. `.badge` shape only. */
  dot?: boolean;
  /**
   * Preference ordinal for the `.choice-badge` shape: 1 → `.choice-1` (primary-soft),
   * 2 → `.choice-2` (surface-3 / muted). Implies (and is only read for) `shape="choice"`.
   */
  choice?: 1 | 2;
  /** Visible label — callers pass an already-translated string. */
  children: React.ReactNode;
  /** Native hover hint (e.g. the longer explanation behind a terse warning tag). */
  title?: string;
  className?: string;
}

export function Tag({
  shape = "badge",
  variant = "primary",
  dot = false,
  choice,
  children,
  title,
  className,
}: TagProps) {
  if (shape === "choice") {
    return (
      <span className={cn("choice-badge", choice === 2 ? "choice-2" : "choice-1", className)} title={title}>
        {children}
      </span>
    );
  }

  const tokens = VARIANT_TOKENS[variant];
  const style: CSSProperties = { color: tokens.color, background: tokens.background };
  return (
    <span className={cn("badge", className)} style={style} title={title}>
      {dot && <span className="badge-dot" style={{ background: tokens.color }} />}
      {children}
    </span>
  );
}
